import { gql } from '@apollo/client';
import {
  Box,
  Button,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldErrors, useForm, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Select, { OptionProps } from 'react-select';
import { z } from 'zod';
import { ErrorAlert } from '~components/ui/Alert';
import { ArrowLeftIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { fromError, getErrorMessage } from '~utils/errors';
import { ensure } from '~utils/types';
import {
  useCreateConfigFromTemplateModalQuery,
  useCreateConfigFromTemplateMutation,
} from './__generated__/CreateConfigurationFromTemplateWizard.graphql';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: (id: string) => Promise<void> | void;
}

const CreateFromTemplateFormData = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  configurationId: z.string().uuid(),
});
type CreateFromTemplateFormData = z.infer<typeof CreateFromTemplateFormData>;

const platformSelectOption = selectOptionBaseSchema;
type PlatformSelectOption = z.TypeOf<typeof platformSelectOption>;

export function CreateConfigFromTemplateModal({ isOpen, onCancel, onSuccess }: Props) {
  const toast = useToast();
  const { customer } = useParams();
  const [currentModalState, setCurrentModalState] = useState<0 | 1>(0);
  const customerHandle = ensure(customer);

  const { data, loading } = useCreateConfigFromTemplateModalQuery({
    variables: {
      customerHandle,
    },
  });

  const {
    getValues,
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateFromTemplateFormData>({
    resolver: zodResolver(CreateFromTemplateFormData),
    mode: 'onChange',
  });

  const { t } = useTranslation();
  const initialFocusRef = useRef<HTMLInputElement | null>(null);
  const { ref: nameInputRef } = register('name');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isDuplicate, setIsDuplicate] = useState(false);

  useEffect(() => {
    if (initialFocusRef.current) {
      initialFocusRef.current.focus();
    }
  }, []);

  const options = useMemo<PlatformSelectOption[] | undefined>(() => {
    const templates = data?.customerByHandle?.templateConfigurations;
    if (!templates) return undefined;
    const mapped = templates.map((templateConfig) => ({
      label: templateConfig.platform.trim(),
      value: templateConfig.id,
    }));

    const sorted = [...mapped].sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
    return sorted;
  }, [data?.customerByHandle?.templateConfigurations]);

  const [cloneDisplayConfiguration] = useCreateConfigFromTemplateMutation({
    refetchQueries: ['CustomerConfigurationsPage'],
    awaitRefetchQueries: true,
  });

  const handleCreateConfig = useCallback(
    async ({ name, description, configurationId }: CreateFromTemplateFormData) => {
      try {
        const { data: config } = await cloneDisplayConfiguration({
          variables: {
            configurationId,
            name,
            description,
            customerId: data?.customerByHandle?.id as string,
          },
          refetchQueries: ['CustomerConfigurationsPage'],
          awaitRefetchQueries: true,
        });

        if (!config || !config.cloneDisplayConfiguration.configuration) {
          onCancel();
          throw new Error('Clone playlist failed.');
        }
        await onSuccess?.(config.cloneDisplayConfiguration.configuration.id);
        toast({
          status: 'success',
          title: t('createConfigurationSuccess'),
          description: t('createConfigurationSuccessDesc', { oldConfig: name }),
        });
        onCancel();
      } catch (err: unknown) {
        if (err instanceof Error) {
          if (err.message.includes('CONFIGURATION_DUPLICATE_NAME')) {
            setIsDuplicate(true);
            return;
          }

          const errDescription = getErrorMessage(err, {});
          toast({
            status: 'error',
            title: t('createConfigurationErr'),
            description: errDescription ?? fromError(err, 'CreateConfiguration'),
          });

          if (!errDescription) {
            onCancel();
          }
        } else {
          toast({
            status: 'error',
            title: t('createConfigurationErr'),
            description: String(err),
          });
          onCancel();
        }
      }
    },
    [onCancel, onSuccess, toast, cloneDisplayConfiguration, t, data?.customerByHandle?.id],
  );

  const checkIsSubmitDisabled = () => {
    const values = getValues();
    const isDisabled =
      !values.name ||
      !values.configurationId ||
      !!errors.name ||
      !!errors.configurationId ||
      isSubmitting;
    setIsSubmitDisabled(isDisabled);
    if (isDuplicate && values.name) setIsDuplicate(false);
  };

  return (
    <Modal
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onCancel}
      size={currentModalState === 1 ? '6xl' : '3xl'}
    >
      <ModalOverlay />
      <form onSubmit={handleSubmit(handleCreateConfig)}>
        <ModalContent
          overflow="hidden"
          height={currentModalState === 1 ? '85vh' : 'auto'}
          display="flex"
          flexDirection="column"
        >
          <ModalHeader>
            {currentModalState === 1 && (
              <IconButton
                aria-label="Go Back"
                icon={<ArrowLeftIcon />}
                onClick={() => setCurrentModalState(0)}
                variant="ghost"
              />
            )}
            {t('createConfiguration')}
          </ModalHeader>
          <ModalCloseButton onClick={onCancel} />
          <ModalBody overflowY="auto" flex="1">
            <ConfigDetailsModalContent
              control={control}
              errors={errors}
              loading={loading}
              options={options}
              register={register}
              nameInputRef={nameInputRef}
              initialFocusRef={initialFocusRef}
              checkIsSubmitDisabled={checkIsSubmitDisabled}
              isDuplicate={isDuplicate}
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
              {t('close')}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              marginLeft="3"
              type="submit"
              isDisabled={isSubmitting || isSubmitDisabled}
              isLoading={isSubmitting}
            >
              {t('create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}

type ConfigDetailsProps = {
  control: Control<CreateFromTemplateFormData>; // from your useForm generic
  errors: FieldErrors<CreateFromTemplateFormData>;
  loading: boolean;
  options: PlatformSelectOption[] | undefined;
  register: UseFormRegister<CreateFromTemplateFormData>;
  nameInputRef: (instance: HTMLInputElement | null) => void;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  checkIsSubmitDisabled: () => void;
  isDuplicate: boolean;
};

function ConfigDetailsModalContent({
  control,
  errors,
  loading,
  options,
  register,
  nameInputRef,
  initialFocusRef,
  checkIsSubmitDisabled,
  isDuplicate,
}: ConfigDetailsProps) {
  return (
    <VStack align={'start'} gap={2}>
      <FormControl isRequired isInvalid={Boolean(errors.name) || isDuplicate}>
        <FormLabel>{t('configurationName')}</FormLabel>
        <Input
          {...register('name', {
            onChange: () => {
              checkIsSubmitDisabled();
            },
            onBlur: () => {
              checkIsSubmitDisabled();
            },
          })}
          ref={(r) => {
            register('name').ref(r);
            nameInputRef(r);
            if (r && !initialFocusRef.current) {
              initialFocusRef.current = r;
            }
          }}
          placeholder={t('configurationNamePlaceholder')}
        />

        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.configurationId)}>
        <FormLabel>{t('selectDisplaySeries')}</FormLabel>
        <Controller
          name="configurationId"
          control={control}
          render={({ field }) => (
            <Select
              value={
                options?.find((opt: PlatformSelectOption) => opt.value === field.value) || null
              }
              options={options}
              components={{
                ...components,
                Option: ManageDisplaySeriesSelectOption,
              }}
              onChange={(selected) => {
                field.onChange(selected?.value);
                setTimeout(checkIsSubmitDisabled, 0);
              }}
              isLoading={loading}
              placeholder={t('select')}
              isClearable={true}
              isSearchable={true}
              menuPlacement="auto"
              isDisabled={loading}
              styles={{
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: 120,
                }),
              }}
            />
          )}
        />
        <FormErrorMessage>{errors.configurationId?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={Boolean(errors.description)}>
        <FormLabel>{t('description')}</FormLabel>
        <Input
          type="textarea"
          placeholder={t('configurationDescPlaceholder')}
          {...register('description')}
        />
        <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
      </FormControl>

      {isDuplicate && <ErrorAlert>{t('duplicateConfigDesc')}</ErrorAlert>}
    </VStack>
  );
}

export function ManageDisplaySeriesSelectOption({
  isSelected,
  innerProps,
  ...rest
}: OptionProps<PlatformSelectOption, false>) {
  const data = rest.data;

  return (
    <Box
      display="flex"
      alignItems="center"
      paddingX="3.5"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{
        background: isSelected ? 'blue.400' : 'blue.50',
      }}
      {...innerProps}
    >
      <chakra.span display="inline-block" flex="1">
        {data.label}
      </chakra.span>
    </Box>
  );
}

CreateConfigFromTemplateModal.graphql = {
  queries: {
    CreateConfigFromTemplateModal: gql`
      query CreateConfigFromTemplateModal($customerHandle: String!) {
        customerByHandle(handle: $customerHandle) {
          id
          ...ConfigDetailSettings_customer
          templateConfigurations {
            id
            platform
            ...ConfigDetailView_config
          }
        }
      }
    `,
  },
  mutations: {
    CreateConfigFromTemplate: gql`
      mutation CreateConfigFromTemplate(
        $configurationId: String!
        $name: String!
        $description: String
        $customerId: String!
      ) {
        cloneDisplayConfiguration(
          configurationId: $configurationId
          name: $name
          description: $description
          customerId: $customerId
        ) {
          configuration {
            ...ConfigurationTable_configuration
          }
        }
      }
    `,
  },
};
